import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import VideocamIcon from '@mui/icons-material/Videocam';
import { Box, Button, Card, CardContent, Chip, CircularProgress, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { AppointmentItem, getNextAppointments } from '../../api/api';
import { useApiClients } from '../../hooks/useAppClients';

interface NextAppointmentsWidgetProps {
  practitionerId?: string;
}

export function NextAppointmentsWidget({ practitionerId }: NextAppointmentsWidgetProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { oystehr } = useApiClients();
  const [appointments, setAppointments] = useState<AppointmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchAppointments = async (): Promise<void> => {
      if (!oystehr || !practitionerId) return;

      setLoading(true);
      setError(null);
      try {
        const response = await getNextAppointments(oystehr, {
          practitioner: practitionerId,
        });

        setAppointments(response.appointments);
      } catch (err: any) {
        console.error('Error fetching next appointments:', err);
        setError('Failed to load appointments');
        setAppointments([]);
      } finally {
        setLoading(false);
      }
    };

    void fetchAppointments();
  }, [oystehr, practitionerId]);

  // Format date using Luxon matching TrackingBoardTableRow behavior:
  // same default timezone as getTimezone() -> 'America/New_York'
  const DISPLAY_TIMEZONE = 'America/New_York';

  const formatDate = (dateString: string): string => {
    const dt = DateTime.fromISO(dateString);
    if (!dt.isValid) return dateString;
    return dt.setZone(DISPLAY_TIMEZONE).setLocale(i18n.language).toFormat('EEE, MMM d');
  };

  const formatTime = (dateString: string): string => {
    const dt = DateTime.fromISO(dateString);
    if (!dt.isValid) return '';
    return dt.setZone(DISPLAY_TIMEZONE).toFormat('h:mm a');
  };

  if (loading) {
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    // Fallback or empty state in case of error
    return (
      <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <CardContent sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <Typography color="error">{error}</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ flexGrow: 1, p: 3, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
            {t('home.appointments.title')}
          </Typography>
          <CalendarTodayIcon color="action" />
        </Box>

        {/* Appointments List */}
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {appointments.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                color: 'text.secondary',
              }}
            >
              <Typography>{t('home.appointments.noAppointments')}</Typography>
            </Box>
          ) : (
            appointments.map((apt) => (
              <Box
                key={apt.id}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: 'background.default',
                  border: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  transition: 'transform 0.1s',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: 2,
                  },
                }}
              >
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <AccessTimeIcon sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
                    <Typography variant="subtitle2" fontWeight="600" color="primary.main">
                      {formatDate(apt.date)} • {formatTime(apt.date)}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <PersonIcon sx={{ fontSize: 18, mr: 0.5, color: 'text.secondary' }} />
                    <Typography variant="body1" fontWeight="500">
                      {apt.patientName}
                    </Typography>
                  </Box>
                  <Chip
                    icon={<VideocamIcon sx={{ fontSize: '1rem !important' }} />}
                    label={t('home.appointments.virtualVisit')}
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 24 }}
                  />
                </Box>
                <Button
                  size="small"
                  variant="text"
                  endIcon={<ArrowForwardIcon />}
                  onClick={() => navigate(`/telemed/appointments/${apt.id}/visit-details`)}
                >
                  {t('home.appointments.join')}
                </Button>
              </Box>
            ))
          )}
        </Box>

        {/* Footer Action */}
        <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            startIcon={<CalendarTodayIcon />}
            onClick={() => navigate('/telemed/appointments')}
          >
            {t('home.appointments.viewAll')}
          </Button>
        </Box>
      </CardContent>
    </Card>
  );
}
